/* eslint-disable react/jsx-one-expression-per-line */
import {
  ContentDeciderContainer,
  Loader,
  getTranslatedText,
  withErrorBoundary,
  ALL_TABLES,
  getAllColumnWidth,
  getAsyncOptions,
  fetchValues,
  getDateOperators,
  getUnifiedConditions,
  getColumnWidth,
  SuperTable,
  getTableRowCount,
  getDefaultRowCount
} from '@nile/components';
import { DEFAULT_TASK_CONDITION } from '@nile/components/src/Pebbles/AdvancedTaskFilter';
import { GET_TASK_LIST, TicketClient } from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import { HomeContext } from '@nile/mangifera/src/Contexts';
import TableView from '@nile/banyan/src/Pages/ServiceCatalog/TableView';
import {
  TextCell,
  TimerCell,
  TagCell,
  HTMLCell,
  NameCell,
  DateTimeCell,
  SourceCell,
  TechnicianCell
} from '@nile/mangifera/src/Components/Task';
import { isEditable } from '@nile/mangifera/src/Pebbles/Favourites';
import {
  getAccessor,
  getType,
  isSortable,
  isFilterable,
  checkDynamicQuery,
  getVariables as getTaskVariables,
  getDefaultValue,
  defaultColumnOrder,
  checkCustomAttribute,
  updateConditions,
  getAttributeName
} from '@nile/mangifera/src/Pebbles/TaskViewContainer';
import { getTabTemplate } from '@nile/mangifera/src/Pebbles/TabManager';
import { cloneDeep } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { SVGIcon } from '@superops/super-icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getDynamicQuery } from '@nile/mangifera/src/Pebbles/TaskToolbar';
import {
  saveColumnWidth,
  saveTableRowCount
} from '@nile/components/src/Utils/TablePersonalization';
import { Tag } from '@superops/ui-design-system';
import {
  WidgetWrapper,
  Header,
  Title,
  Content,
  Actions
} from '../HomeWidgetContainer';
import DeletedView from './DeletedView';
import AccessDenied from './AccessDenied';
import { getEmoji } from '../../Pebbles/Views';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';

function getNewColumns(intl) {
  return [
    {
      name: 'source',
      label: getTranslatedText(intl, 'task.listView.header.source'),
      attributeName: 'workItem.subject',
      filterType: 'text',
      dataType: 'string'
    },
    {
      name: 'workItem.client',
      attributeName: 'workItem.client.name',
      label: getTranslatedText(intl, 'common.client'),
      dataType: 'entity',
      entity: 'client',
      filterType: 'select'
    },
    {
      name: 'workItem.site',
      attributeName: 'workItem.site.name',
      label: getTranslatedText(intl, 'app.common.site'),
      filterType: 'text',
      dataType: 'string'
    }
  ];
}

function TaskViewWidget(props) {
  const {
    widget,
    user,
    meta,
    intl,
    taskViews,
    deleteWidget,
    taskViewMeta
  } = props;

  const viewPermission = widget?.permission;
  const viewExist = taskViews.some((el) => el.id === widget.id);

  const flags = useFlags();
  const paginationFlag = flags?.dynamicPaginationTasks;
  const tableName = ALL_TABLES.tasks;

  const context = useContext(HomeContext);
  const tabContext = useContext(TabContext);
  const [loading, setLoading] = useState(!!widget && viewPermission);
  const [data, setData] = useState([]);
  const [listInfo, setListInfo] = useState({});
  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', columns: [] }
  ]);
  const ROW_COUNT = paginationFlag
    ? getTableRowCount(tableName)
    : getDefaultRowCount(tableName);
  const [rowCount, setRowCount] = useState(ROW_COUNT);

  const taskEntity = meta.find((el) => el.name === 'Task');
  const columns = taskViewMeta?.selectAttributes
    .map((el) => {
      return taskEntity.attributes.find((attr) => attr.name === el);
    })
    .map((el) => {
      return {
        ...el,
        label: el?.label ? getTranslatedText(intl, el?.label) : null
      };
    });

  const currentView =
    viewPermission && viewExist
      ? taskViews.find((el) => el.id === widget.id)
      : widget;
  const options = { pageSize: 20, page: 1, view: { id: widget.id } };

  const navigateToView = () => {
    if (!viewPermission || !viewExist) return;
    tabContext.openTab({
      module: 'WORKSPACE',
      url: `/tasks/${currentView.id}/task-list`
    });
  };

  const getActions = useCallback((col) => {
    switch (col.name) {
      default:
        return {};
    }
  });

  const columnWidthObject = useMemo(() => getAllColumnWidth(tableName), [
    tableName
  ]);

  const fetchViewData = async (personalization, pageOptions) => {
    setLoading(true);
    const allCols = columns.concat(getNewColumns(intl));
    const personalizedColumns = [];
    defaultColumnOrder.forEach((el) => {
      const currentCol = allCols.find((col) => col?.name === el);
      if (currentCol) {
        personalizedColumns.push(currentCol);
      }
    });

    const response = await TicketClient.query({
      query: GET_TASK_LIST,
      variables: {
        listInfo: pageOptions
          ? { ...pageOptions, view: { id: widget.id } }
          : options
      },
      fetchPolicy: 'network-only'
    });
    setLoading(false);
    const cols = personalizedColumns;
    const allColumns =
      cols?.length &&
      cols.map((col) => {
        return {
          Header: col.label,
          header: col.label,
          accessor: getAccessor(col),
          accessorKey: getAccessor(col),
          type: getType(col),
          disableSortBy: true,
          disableFilters: true,
          // disableSortBy: !isSortable(col, taskViewMeta.sortAttributes),
          // disableFilters: !isFilterable(col),
          disableResizing: false,
          className: 'sticky',
          editable: isEditable(col),
          actions: getActions(col),
          size: getColumnWidth(columnWidthObject, col.name),
          field: {
            ...col,
            autoComplete: col.name === 'technician' ? true : col.autoComplete
          },
          helpers: {
            getDefaultValue,
            checkDynamicQuery,
            getAsyncOptions,
            fetchValues,
            getDateOperators,
            getVariables: getTaskVariables,
            getDynamicQuery,
            entity: 'Task',
            checkCustomAttribute
          }
        };
      });
    setColumnGroup([{ Header: 'Name', header: 'name', columns: allColumns }]);
    setData(response?.data?.getTasks?.tasks || []);
    setListInfo(response?.data?.getTasks?.listInfo);
  };

  useEffect(() => {
    if (viewPermission) fetchViewData();
  }, []);

  const defaultColumnCells = {
    HTML: TextCell,
    Text: TextCell,
    Timer: TimerCell,
    Status: TagCell,
    Name: NameCell,
    DateTime: DateTimeCell,
    Source: SourceCell,
    Technician: TechnicianCell
  };

  const deleteButton = (
    <div className="widget-header flex center fg1">
      <Actions className="widget-actions visibilityActions">
        <div onClick={deleteWidget.bind(this, widget)} className="ptr">
          <SVGIcon name="Delete" />
        </div>
      </Actions>
    </div>
  );

  if (loading) {
    return (
      <WidgetWrapper>
        {deleteButton}
        <Loader />
      </WidgetWrapper>
    );
  }
  return (
    <WidgetWrapper>
      <Header>
        <Title onClick={navigateToView}>
          {getEmoji({ ...currentView, section: 'task' })}
          {currentView.name}
          {viewPermission && viewExist && (
            <div className="ml-10">
              <Tag>
                {data?.length}/{listInfo?.totalCount}
              </Tag>
            </div>
          )}
        </Title>
        {deleteButton}
      </Header>
      {!viewPermission ? (
        <AccessDenied />
      ) : (
        <>
          {!viewExist ? (
            <DeletedView />
          ) : (
            <Content>
              <ContentDeciderContainer
                isLoading={loading}
                hasContent={
                  data?.length > 0 && columnGroup[0]?.columns?.length > 0
                }
                mainContent={
                  <SuperTable
                    noBulkSelect
                    context={{ viewConditions: null }}
                    columns={columnGroup}
                    data={data}
                    setData={setData}
                    rowCount={rowCount}
                    refreshTable={() => {}}
                    defaultColumnCells={defaultColumnCells}
                    isLoading={loading}
                    fullHeight
                    user={user}
                    setRowCount={setRowCount}
                    saveTableRowCount={saveTableRowCount}
                    columnWidthPersonalization
                    saveColumnWidth={saveColumnWidth}
                    tableName={tableName}
                    getAttributeName={getAttributeName}
                  />
                }
                newEmptyContainer
                loadingContent={<Loader />}
                notFoundURL={`${envConfig.imageRoot}svg/not-found.svg`}
              />
            </Content>
          )}
        </>
      )}
    </WidgetWrapper>
  );
}

export default withErrorBoundary(TaskViewWidget);
